import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { ErrorT } from '@app/services/rtkQuery/baseQuery';
import {
  useAddIntegration1CMutation,
  useTestIntegration1CMutation,
} from '@app/services/rtkQuery/SocialIntegrations/api';
import type { NSocialIntegrations } from '@app/services/rtkQuery/SocialIntegrations/types';

import { normalizeWarehouses } from '@pages/Integrations/IntegrationsList/components/Add1CIntegrationModal/utils';

import type {
  AuthFormValues,
  Step,
  UseAdd1CIntegrationModalProps,
  WarehouseItem,
} from '@pages/Integrations/IntegrationsList/components/Add1CIntegrationModal/types';

const { useForm } = Form;

export const useAdd1CIntegrationModal = ({
  isOpen,
  onCancel,
  afterSave,
}: UseAdd1CIntegrationModalProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [form] = useForm<AuthFormValues>();
  const { submit, resetFields } = form;
  const [step, setStep] = useState<Step>('auth');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [currentIntegration, setCurrentIntegration] =
    useState<NSocialIntegrations.Model1cWarehouseIntegration | null>(null);

  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>(
    []
  );
  const [isSaveValidationVisible, setIsSaveValidationVisible] = useState(false);

  const [
    testIntegration1c,
    { isLoading: isTestingConnection, reset: resetTestConnection },
  ] = useTestIntegration1CMutation();

  const [
    addIntegration1c,
    { isLoading: isSavingIntegration, reset: resetAddIntegration },
  ] = useAddIntegration1CMutation();

  const handleReset = useCallback(() => {
    setStep('auth');
    setConnectionError(null);
    setWarehouses([]);
    setSelectedWarehouseIds([]);
    setIsSaveValidationVisible(false);
    resetFields();
    resetTestConnection();
    resetAddIntegration();
  }, [resetFields, resetAddIntegration, resetTestConnection]);

  const handleToggleWarehouse = useCallback(
    (warehouseGuid: string, checked: boolean) => {
      setSelectedWarehouseIds((prev) => {
        if (checked) {
          return prev.includes(warehouseGuid) ? prev : [...prev, warehouseGuid];
        }

        return prev.filter((id) => id !== warehouseGuid);
      });

      setIsSaveValidationVisible(false);
    },
    []
  );

  const isPrimaryActionLoading = isTestingConnection || isSavingIntegration;

  const isPrimaryActionDisabled = useMemo(() => {
    if (!isOpen) return true;
    if (step === 'auth') return false;

    return selectedWarehouseIds.length === 0 || isPrimaryActionLoading;
  }, [isOpen, isPrimaryActionLoading, selectedWarehouseIds.length, step]);

  const handleContinue = useCallback(
    async ({
      integrationName,
      serviceUrl,
      login,
      password,
    }: AuthFormValues) => {
      setConnectionError(null);
      setIsSaveValidationVisible(false);

      try {
        const response = await testIntegration1c({
          ActiveBranchId,
          Name: integrationName,
          Server: serviceUrl,
          Username: login,
          Password: password,
        }).unwrap();

        setCurrentIntegration(response);

        const normalized = normalizeWarehouses(response.Warehouses);

        setWarehouses(normalized);
        setSelectedWarehouseIds(
          normalized
            .filter(({ isSelected }) => isSelected)
            .map(({ guid1C }) => guid1C)
        );

        setStep('settings');
      } catch (error) {
        const err = error as ErrorT;
        const message =
          !!err && 'data' in err
            ? err.data
            : 'Не удалось установить соединение сервера с 1С.';

        setConnectionError(message);
        showNotification(error);
      }
    },
    [ActiveBranchId, testIntegration1c, showNotification]
  );

  const handleSave = useCallback(async () => {
    if (selectedWarehouseIds.length === 0) {
      setIsSaveValidationVisible(true);
      return;
    }

    if (!currentIntegration) return;

    const { Name, Server, Username, Password } = currentIntegration;

    const Warehouses = warehouses.map(({ guid1C, name }: WarehouseItem) => ({
      Guid1C: guid1C,
      WarehouseName: name,
      IsSeleted: selectedWarehouseIds.includes(guid1C),
    }));

    try {
      await addIntegration1c({
        ActiveBranchId,
        Name,
        Server,
        Username,
        Password,
        Warehouses,
      }).unwrap();

      onCancel();
      showNotification('Интеграция успешно добавлена.', 'success');
      afterSave?.();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    addIntegration1c,
    onCancel,
    selectedWarehouseIds,
    warehouses,
    showNotification,
    currentIntegration,
    afterSave,
  ]);

  const handlePrimaryAction = useCallback(() => {
    if (step === 'auth') {
      submit();
      return;
    }

    void handleSave();
  }, [handleSave, step, submit]);

  const warehousesList = useMemo(
    () =>
      warehouses.map(({ guid1C, name }) => ({
        id: guid1C,
        name,
      })),
    [warehouses]
  );

  return {
    form,
    step,
    warehousesList,
    selectedWarehouseIds,
    isPrimaryActionLoading,
    isPrimaryActionDisabled,
    isSaveValidationVisible,
    handleReset,
    handlePrimaryAction,
    handleToggleWarehouse,
    connectionError,
    handleContinue,
  };
};
